<template>
  <div class="page-shell">
    <VendorDashboardHeader title="Create Quotation" />

    <div>
      <v-form @submit.prevent="handleSubmit" class="quotation-form">
        <div class="mb-4">
          <AppTextField v-model="quotationData.name" label="Name" placeholder="Template Name" required />
        </div>
        <AppTextArea v-model="quotationData.description" label="Description" placeholder="Enter template description..."
          rows="2" required />

        <div class="form-actions">
          <AppButtonFlat type="submit" color="primary" prepend-icon="mdi-check" :loading="isSubmitting"
            :disabled="isSubmitting || !isValid" block>
            Create
          </AppButtonFlat>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import VendorDashboardHeader from "@/modules/vendor/components/VendorDashboardHeader.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { saveQuotationTemplate } from "@/modules/vendor/services/quotation-templates.api";

const router = useRouter();
const snackbar = useSnackbarStore();

const props = defineProps<{
  templateId?: string | number;
}>();

const quotationData = ref({
  name: "",
  description: "",
});

const isSubmitting = ref(false);

const isValid = computed(() => {
  return Boolean(quotationData.value.name);
});

async function handleSubmit() {
  if (isSubmitting.value || !isValid.value) return;

  isSubmitting.value = true;

  try {
    const payload: Record<string, any> = {
      id: props.templateId ?? null,
      name: quotationData.value.name,
      description: quotationData.value.description || null,
      is_active: true,
    };

    const response = await saveQuotationTemplate(payload);
    const templateId =
      response?.data?.id ??
      response?.id ??
      response?.data?.template?.id ??
      response?.template?.id;

    if (!templateId) {
      throw new Error("Template ID missing from saveQuotationTemplate response.");
    }

    router.back();
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to create quotation.";
    snackbar.error(message);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 14px;
}

.form-container {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
}

.form-actions {
  margin-top: 12px;
}
</style>
